import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Icon from '../../../components/AppIcon';
import {
  ambientMusicStorageKey,
  hasAmbientMusic,
  normalizeAmbientMusic,
} from '../utils/ambientMusic';
import { resolveWebCreatorMediaUrl } from '../utils/webCreatorMedia';

const SiteAmbientMusicContext = createContext(null);

export function SiteAmbientMusicProvider({
  config,
  siteSlug,
  playbackEnabled = false,
  children,
}) {
  const normalized = useMemo(() => normalizeAmbientMusic(config), [config]);
  const audioSrc = useMemo(
    () => resolveWebCreatorMediaUrl(normalized.audioUrl),
    [normalized.audioUrl],
  );
  const active = hasAmbientMusic(normalized);
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(ambientMusicStorageKey(siteSlug)) === '1';
    } catch {
      return false;
    }
  });
  const [needsGesture, setNeedsGesture] = useState(false);

  const persistMuted = useCallback(
    (next) => {
      setMuted(next);
      try {
        window.sessionStorage.setItem(ambientMusicStorageKey(siteSlug), next ? '1' : '0');
      } catch {
        /* ignore */
      }
    },
    [siteSlug],
  );

  const toggleMute = useCallback(() => {
    persistMuted(!muted);
  }, [muted, persistMuted]);

  const tryPlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !playbackEnabled || !active || muted) return;
    el.volume = normalized.volume;
    const p = el.play();
    if (p?.catch) {
      p.catch(() => setNeedsGesture(true));
    }
  }, [active, muted, normalized.volume, playbackEnabled]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !active) return undefined;
    el.volume = normalized.volume;
    if (!playbackEnabled || muted) {
      el.pause();
      return undefined;
    }
    tryPlay();
    return undefined;
  }, [active, muted, normalized.volume, normalized.audioUrl, playbackEnabled, tryPlay]);

  useEffect(() => {
    if (!needsGesture || !playbackEnabled || muted || !active) return undefined;
    const onGesture = () => {
      tryPlay();
      setNeedsGesture(false);
    };
    window.addEventListener('pointerdown', onGesture, { once: true, passive: true });
    return () => window.removeEventListener('pointerdown', onGesture);
  }, [needsGesture, playbackEnabled, muted, active, tryPlay]);

  const value = useMemo(
    () =>
      active
        ? {
            active: true,
            muted,
            toggleMute,
            playbackEnabled,
          }
        : { active: false },
    [active, muted, toggleMute, playbackEnabled],
  );

  return (
    <SiteAmbientMusicContext.Provider value={value}>
      {active && playbackEnabled && audioSrc ? (
        <audio
          key={audioSrc}
          ref={audioRef}
          src={audioSrc}
          loop
          preload="auto"
          aria-hidden
        />
      ) : null}
      {children}
    </SiteAmbientMusicContext.Provider>
  );
}

export function AmbientMusicMenuControl({ className = '' }) {
  const ctx = useContext(SiteAmbientMusicContext);
  if (!ctx?.active) return null;

  const label = ctx.muted ? 'Activer la musique' : 'Couper la musique';
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center p-2 rounded-lg shrink-0 transition-colors hover:bg-black/5 ${className}`.trim()}
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        ctx.toggleMute();
      }}
    >
      <Icon name={ctx.muted ? 'VolumeX' : 'Volume2'} size={20} strokeWidth={1.75} />
    </button>
  );
}

/** Quand il n’y a pas de menu page (ex. Jardins desktop). */
export function AmbientMusicFixedControl({ enabled }) {
  const ctx = useContext(SiteAmbientMusicContext);
  if (!enabled || !ctx?.active) return null;
  return (
    <div className="pointer-events-none fixed top-3 right-3 z-[60]">
      <div className="pointer-events-auto rounded-full bg-white/90 shadow-md border border-black/10 backdrop-blur-sm">
        <AmbientMusicMenuControl className="rounded-full" />
      </div>
    </div>
  );
}
